import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { ExtendedFirebaseInstance, useFirebase } from 'react-redux-firebase';

import { IAppState } from '../store/reducers';
import { selectUserAccessToken } from '../store/selectors/auth';

export type TAuthorizedFetcher<T> = (token?: string) => Promise<T>;

export type TAuthorizedFirebase = Pick<ExtendedFirebaseInstance, 'reloadAuth' | 'logout'>;

export interface IAuthorizedFetchDeps {
  firebase: TAuthorizedFirebase;
  getState: () => IAppState;
}

export interface IAuthorizedFetchOptions {
  signal?: AbortSignal;
}

const pendingReloads = new WeakMap<TAuthorizedFirebase, Promise<void>>();

const isUnauthorized = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 401;

const reloadAuthOnce = (firebase: TAuthorizedFirebase): Promise<void> => {
  const pending = pendingReloads.get(firebase);
  if (pending) {
    return pending;
  }

  const reload = firebase.reloadAuth().finally(() => {
    pendingReloads.delete(firebase);
  });
  pendingReloads.set(firebase, reload);

  return reload;
};

const logoutSafely = (firebase: TAuthorizedFirebase): Promise<void> => firebase.logout().catch(() => undefined);

export const runAuthorizedFetch = async <T>(
  { firebase, getState }: IAuthorizedFetchDeps,
  fetcher: TAuthorizedFetcher<T>,
  { signal }: IAuthorizedFetchOptions = {},
): Promise<T> => {
  const storeToken = selectUserAccessToken(getState());

  try {
    return await fetcher(storeToken);
  } catch (error) {
    if (!isUnauthorized(error) || signal?.aborted) {
      throw error;
    }

    if (selectUserAccessToken(getState()) === storeToken) {
      try {
        await reloadAuthOnce(firebase);
      } catch {
        if (!signal?.aborted) {
          await logoutSafely(firebase);
        }
        throw error;
      }
    }

    if (signal?.aborted) {
      throw error;
    }

    try {
      return await fetcher(selectUserAccessToken(getState()));
    } catch (retryError) {
      if (isUnauthorized(retryError) && !signal?.aborted) {
        await logoutSafely(firebase);
      }
      throw retryError;
    }
  }
};

export default function useAuthorizedFetch() {
  const firebase = useFirebase();
  const store = useStore<IAppState>();

  return useCallback(
    <T>(fetcher: TAuthorizedFetcher<T>, options?: IAuthorizedFetchOptions) =>
      runAuthorizedFetch({ firebase, getState: store.getState }, fetcher, options),
    [firebase, store],
  );
}
